import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import ChatHeader from "./ChatHeader";
import { dayKey, formatDayLabel, formatMessageTime } from "./chatFormat";
import Authorization from "../../utils/authorization";
import { useChatStore } from "./store/useChatStore";

/** Splits an ascending message list into day buckets for date separators. */
const groupByDay = (messages) => {
  const groups = [];
  messages.forEach((message) => {
    const key = dayKey(message?.created_at);
    const current = groups[groups.length - 1];
    if (current && current.key === key) {
      current.messages.push(message);
    } else {
      groups.push({ key, messages: [message] });
    }
  });
  return groups;
};

const ChatContainer = ({ onBack }) => {
  const {
    messages,
    getMessages,
    isMessagesLoading,
    selectedUser,
    subscribeToMessages,
    unsubscribeFromMessages,
    sendMessage,
  } = useChatStore();
  const userId = Authorization.getUserId();
  const listRef = useRef(null);

  const [text, setText] = useState("");

  useEffect(() => {
    if (selectedUser) {
      getMessages(selectedUser?.conversation_id);
    }

    subscribeToMessages();

    return () => unsubscribeFromMessages();
  }, [selectedUser, getMessages, subscribeToMessages, unsubscribeFromMessages]);

  const visibleMessages = useMemo(
    () => (messages || []).filter((item) => item?.message?.trim()),
    [messages],
  );

  // Keep the newest message in view as the thread grows. Scrolling the list
  // itself rather than a sentinel avoids dragging ancestor scroll containers.
  useEffect(() => {
    const list = listRef.current;
    if (list) {
      list.scrollTop = list.scrollHeight;
    }
  }, [visibleMessages.length, isMessagesLoading]);

  const handleSendMessage = async () => {
    if (!text.trim()) return;
    const messageData = {
      recipientId: selectedUser?.recipient?.id,
      content: text,
    };
    try {
      const sent = await sendMessage(messageData);
      if (sent) setText("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      height={"100%"}
      minHeight={0}
      bgcolor={"#f4f5f7"}
    >
      <ChatHeader onBack={onBack} />

      <Box
        ref={listRef}
        flex={1}
        overflow={"auto"}
        minHeight={0}
        px={{ xs: 1.5, md: 3 }}
        py={2}
      >
        {isMessagesLoading ? (
          <Box display={"flex"} justifyContent={"center"} pt={4}>
            <CircularProgress size={28} color="primary" />
          </Box>
        ) : visibleMessages.length === 0 ? (
          <Box
            height={"100%"}
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            textAlign={"center"}
            gap={1}
            px={2}
          >
            <ForumRoundedIcon sx={{ fontSize: 46, color: "#ffb877" }} />
            <Typography fontWeight={600}>No messages yet</Typography>
            <Typography fontSize={13} color="text.secondary">
              Say hello to {selectedUser?.recipient?.name || "this member"} and
              start the conversation.
            </Typography>
          </Box>
        ) : (
          groupByDay(visibleMessages).map((group) => (
            <Box key={group.key || "undated"}>
              {group.key && (
                <Box display={"flex"} justifyContent={"center"} my={1.5}>
                  <Chip
                    label={formatDayLabel(group.messages[0]?.created_at)}
                    size="small"
                    sx={{ bgcolor: "#e6e8ec", fontSize: 11, fontWeight: 600 }}
                  />
                </Box>
              )}

              {group.messages.map((message, index) => {
                const isMine = Number(message?.sender_id) === Number(userId);
                const previous = group.messages[index - 1];
                const startsRun =
                  !previous ||
                  Number(previous.sender_id) !== Number(message.sender_id);

                return (
                  <Box
                    key={message.id || `${message.created_at}-${index}`}
                    display={"flex"}
                    justifyContent={isMine ? "flex-end" : "flex-start"}
                    mt={startsRun ? 1.25 : 0.5}
                  >
                    <Box
                      maxWidth={{ xs: "85%", md: "68%" }}
                      px={1.5}
                      py={1}
                      bgcolor={isMine ? "primary.main" : "#fff"}
                      color={isMine ? "#fff" : "text.primary"}
                      border={isMine ? "none" : "1px solid #ececec"}
                      sx={{
                        borderRadius: 2,
                        borderTopRightRadius: isMine && startsRun ? 0 : 8,
                        borderTopLeftRadius: !isMine && startsRun ? 0 : 8,
                        boxShadow: "0 1px 1px rgba(0,0,0,0.04)",
                      }}
                    >
                      <Typography
                        fontSize={14}
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                      >
                        {message?.message}
                      </Typography>
                      <Typography
                        fontSize={10}
                        textAlign={"right"}
                        mt={0.25}
                        sx={{ opacity: 0.7 }}
                      >
                        {formatMessageTime(message?.created_at)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>

      <Box
        display={"flex"}
        alignItems={"flex-end"}
        gap={1}
        px={{ xs: 1.5, md: 2 }}
        py={1.5}
        bgcolor={"#fff"}
        borderTop={"1px solid #ececec"}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              handleSendMessage();
            }
          }}
          placeholder="Type a message..."
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6, bgcolor: "#fff" } }}
        />
        <IconButton
          color="primary"
          aria-label="Send message"
          disabled={!text.trim()}
          onClick={handleSendMessage}
          sx={{
            bgcolor: "primary.main",
            color: "#fff",
            "&:hover": { bgcolor: "#e57300" },
            "&.Mui-disabled": { bgcolor: "#f0f0f0", color: "#bdbdbd" },
          }}
        >
          <SendRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatContainer;
